import React, { Component, Suspense } from 'react';
import { observer } from 'mobx-react';
import MainSideBar from './MainSideBar/MainSideBar';
import CenterContent from './CenterContent/CenterContent';
import RightBar from './RightBar/RightBar';
import UserSettingsDrawer from '../SettingsDrawer/UserSettingsDrawer';
import HelpDrawer from '../HelpDrawer';
import NoInternetMessage from '../NoInternetMessage';
import { APP_STATE } from '../../constants';
import IsReadyErrorMessage from '../IsReadyErrorMessage';
import ProtectedRoute from '../ProtectedRoute';
import ErrorBoundary from '../ErrorBoundary';
import { Permissions } from '@whatsper/texterchat-common';
import NotificationsDrawer from '../Notifications/NotificationsDrawer';
import AutoDisconnectModalContent from '../ModalContents/AutoDisconnectModalContent';
import SECTION from '../../store/NavigationStore/Section';
import { onlineStatus } from '../../backend';
import EmptyModal from '../Chat/common/EmplyModal';
import { withStore } from '../../store/rootStore';
import { Route, Switch } from 'react-router-dom';
import lazyWithRetry from '../../helpers/lazyWithRetry';
import LoadingScreen from '../LoadingScreen';
import { withTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getDirection } from '../../helpers';
import MountOnOpen from '../Base/Utils/MountOnOpen';
import TopContent from './TopContent/TopContent';
import { appSendsBackEvent } from './helpers';
import MobileTopBar from './TopContent/atoms/MobileTopBar';
import './MainPageLayout.scss';
const BotAdmin = lazyWithRetry(() => import('../BotAdmin/BotAdmin'));
const DashboardPage = lazyWithRetry(() => import('../Dashboard/DashboardPage'));
const ScenariosManager = lazyWithRetry(() => import('../ScenariosManager'));
const SettingsDrawer = lazyWithRetry(() => import('../SettingsDrawer/SettingsDrawer'));
const WizardModal = lazyWithRetry(() => import('../WizardModal'));
const TopMobileDrawer = lazyWithRetry(() => import('../TopMobileDrawer/TopMobileDrawer'));
const NewMessageModal = lazyWithRetry(() => import('../NewMessageModal/NewMessageModal'));
const BulkReportsDrawer = lazyWithRetry(() => import('../BulkReportsDrawer/BulkReportsDrawer'));
const NotificationSubscribeExample = lazyWithRetry(() => import('../NotificationSubscribeExample'));
const TemplateMessagesDrawer = lazyWithRetry(() => import('./TemplateMessagesDrawer'));
const TemplateGenerator = lazyWithRetry(() => import('../Chat/PreparedMessages/TemplateMessages/components/TemplateGenerator'));
const QuickRepliesSettingsModal = lazyWithRetry(() => import('../Chat/PreparedMessages/QuickReplies/QuickRepliesSettingsModal'));
const EditProfileDrawer = lazyWithRetry(() => import('../EditProfileDrawer/EditProfileDrawer'));
const ChatSettingsRoute = lazyWithRetry(() => import('../../routes/settings/ChatSettingsRoute'));
const isWorkspacePage = (pathname) => pathname === '/dashboard' ||
    pathname === '/dashboard/' ||
    pathname.startsWith('/bot-admin') ||
    pathname.startsWith('/manage/scenarios');
class MainPageLayout extends Component {
    constructor(props) {
        super(props);
        Object.defineProperty(this, "usesAppBackEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: appSendsBackEvent()
        });
        Object.defineProperty(this, "componentDidMount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.init();
            }
        });
        Object.defineProperty(this, "componentDidUpdate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (previousProps) => {
                const wasWorkspacePage = isWorkspacePage(previousProps.location.pathname);
                const onWorkspacePage = isWorkspacePage(this.props.location.pathname);
                if (wasWorkspacePage !== onWorkspacePage) {
                    if (!this.usesAppBackEvent) {
                        if (onWorkspacePage) {
                            window.removeEventListener('popstate', this.handleBackButton);
                        }
                        else {
                            window.addEventListener('popstate', this.handleBackButton);
                        }
                    }
                    this.setInitSectionsOpen();
                }
            }
        });
        Object.defineProperty(this, "componentWillUnmount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.state.midnightInterval) {
                    clearInterval(this.state.midnightInterval);
                }
                window.removeEventListener('popstate', this.handleBackButton);
                window.removeEventListener('goBack', this.closeTopmost);
                window.removeEventListener('resize', this.setAppHeight);
            }
        });
        /**
         * ============================================================================================
         *  ANDROID BACK BUTTON — READ THIS BEFORE TOUCHING BACK HANDLING, HISTORY OR ROUTING
         * ============================================================================================
         *  When the user presses the hardware back button, the mobile app (mobile-app repo,
         *  src/components/screens/Main.tsx) does two things: it calls webView.goBack(), and it
         *  dispatches a 'goBack' window event into this page. Apps from 1.2.4 (June 2024) on send
         *  that event on every press.
         *
         *  We react to the 'goBack' event, not to 'popstate', because 'popstate' is unreliable in
         *  the app. webView.goBack() only fires it when the WebView has a history entry to go back
         *  to, and the WebView skips entries a page pushed without a user tap. Opening a chat
         *  replaces the current entry instead of adding one, so after a cold start, a notification
         *  or a deep link there is usually nothing to go back to and 'popstate' never comes. Earlier
         *  versions of this handler relied on 'popstate', which is why back kept breaking whenever
         *  routing or links changed somewhere else in the app.
         *
         *  So inside the app, 'goBack' runs closeTopmost, which closes settings if they are open,
         *  otherwise the open chat, and if nothing was open tells the app to minimize
         *  ('hitHistoryRoot'). 'popstate' still re-pushes the current path so the URL stays put, but
         *  it must not close anything there: both events arrive for the same press, and handling
         *  both would close the chat and then minimize the app. Browsers and apps older than 1.2.4
         *  have no 'goBack' event, so for them 'popstate' still does the closing.
         *
         *  Because the app path does not depend on browser history, changing links, routes,
         *  redirects or where chats are opened from will not break it. What it does depend on: the
         *  app keeps sending 'goBack', that event stays wired to closeTopmost, and anything new that
         *  back should close is added to closeTopmost.
         * ============================================================================================
         */
        Object.defineProperty(this, "handleBackButton", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { history, location } = this.props;
                if (!this.usesAppBackEvent && isWorkspacePage(window.location.pathname))
                    return;
                history.push(location.pathname);
                if (!this.usesAppBackEvent) {
                    this.closeTopmost();
                }
            }
        });
        Object.defineProperty(this, "closeTopmost", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { store } = this.props;
                let closedSomething = false;
                if (store.navigation.sectionOpen[SECTION.settings]) {
                    store.navigation.toggleSection(SECTION.settings, false);
                    closedSomething = true;
                }
                if (store.navigation.sectionOpen[SECTION.help]) {
                    store.navigation.toggleSection(SECTION.help, false);
                    closedSomething = true;
                }
                if (store.navigation.sectionOpen[SECTION.userSettings]) {
                    store.navigation.toggleSection(SECTION.userSettings, false);
                    closedSomething = true;
                }
                if (store.notificationsStore.isOpen) {
                    store.notificationsStore.setIsOpen(false);
                    closedSomething = true;
                }
                if (isWorkspacePage(this.props.location.pathname)) {
                    if (!closedSomething) {
                        this.props.history.push('/');
                    }
                    return;
                }
                if (store.activeChatStore.chat) {
                    store.activeChatStore.setActiveChatId();
                    closedSomething = true;
                }
                if (!closedSomething) {
                    if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(JSON.stringify({
                            event: 'hitHistoryRoot',
                        }));
                    }
                }
            }
        });
        Object.defineProperty(this, "setInitSectionsOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { match, store } = this.props;
                const routeSection = match.params.section || null;
                const sectionOpen = {};
                for (const sName in SECTION) {
                    sectionOpen[sName] = routeSection === SECTION[sName];
                }
                store.navigation.initSectionOpen(sectionOpen);
            }
        });
        Object.defineProperty(this, "setAppHeight", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            }
        });
        Object.defineProperty(this, "midnightReloadHandle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.props.location.pathname.startsWith('/bot-admin'))
                    return;
                if (new Date().getHours() === 4) {
                    window.location.reload();
                }
            }
        });
        Object.defineProperty(this, "createChat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (chatId, account) => {
                this.props.store.activeChatStore.setActiveChatByChannelId(chatId, account.name, account.id);
            }
        });
        this.state = {
            midnightInterval: null,
        };
    }
    init() {
        if (this.usesAppBackEvent || !isWorkspacePage(this.props.location.pathname)) {
            window.addEventListener('popstate', this.handleBackButton);
        }
        if (this.usesAppBackEvent) {
            window.addEventListener('goBack', this.closeTopmost);
        }
        window.addEventListener('resize', this.setAppHeight);
        const midnightInterval = setInterval(() => this.midnightReloadHandle(), 3600000);
        this.setState({ midnightInterval });
        this.setInitSectionsOpen();
        this.setAppHeight();
    }
    render() {
        var _a;
        const { statusStore, appState, navigation, wizardStore, loginStore, agentsStore, templateMessagesStore, quickRepliesStore, bulkReports, partnerTheme } = this.props.store;
        const { logoutCountdown } = statusStore;
        const { logoutCountdownStart, logoutCountdownLength } = (_a = statusStore.settings) !== null && _a !== void 0 ? _a : {};
        const { sectionOpen } = navigation;
        const showMobilePageHeader = isWorkspacePage(this.props.location.pathname) && !this.props.location.pathname.startsWith('/bot-admin');
        return (React.createElement("div", { className: "main-page-container" },
            React.createElement("div", { className: classNames("main-page-layout", {
                    "rtl": getDirection() === "rtl",
                }) },
                React.createElement(MainSideBar, { onNavigate: () => navigation.setWorkspaceOverlay(), settingsAction: () => navigation.setWorkspaceOverlay(sectionOpen.settings ? undefined : 'settings'), helpAction: () => navigation.setWorkspaceOverlay(sectionOpen.help ? undefined : 'help'), helpOpen: sectionOpen.help, toggleTemplateMessagesDrawer: () => navigation.setWorkspaceOverlay(navigation.isTemplateMessagesDrawerOpen ? undefined : 'templates'), templateMessagesActive: navigation.isTemplateMessagesDrawerOpen || navigation.isBulkReportsDrawerOpen, drawer: !!sectionOpen.settings || navigation.isEditProfileDrawerOpen, hasPermissions: agentsStore.hasPermissions, hasScheduledReports: bulkReports.hasScheduledReports, partnerTheme: partnerTheme }),
                React.createElement(HelpDrawer, { isOpen: !!sectionOpen.help, onClose: () => navigation.toggleSection(SECTION.help, false) }),
                React.createElement(NotificationsDrawer, null),
                React.createElement(UserSettingsDrawer, { isOpen: !!sectionOpen.userSettings, onClose: () => navigation.toggleSection(SECTION.userSettings, false) }),
                React.createElement(MountOnOpen, { open: !!sectionOpen.settings },
                    React.createElement(SettingsDrawer, { className: "settings-drawer", onCloseDrawer: () => navigation.toggleSection(SECTION.settings, false), isOpen: !!sectionOpen.settings, toggleTemplateDrawer: () => {
                            templateMessagesStore.toggleNewTemplateDrawer();
                            navigation.toggleSection(SECTION.settings, false);
                        } })),
                React.createElement(MountOnOpen, { open: wizardStore.isWizardOpen },
                    React.createElement(WizardModal, { closeModal: wizardStore.setWizardIsOpen, visible: wizardStore.isWizardOpen })),
                React.createElement(MountOnOpen, { open: templateMessagesStore.isNewTemplateDrawer },
                    React.createElement(TemplateGenerator, null)),
                React.createElement(MountOnOpen, { open: quickRepliesStore.isSettingsModalOpen },
                    React.createElement(QuickRepliesSettingsModal, null)),
                sectionOpen.mobileDrawer ? (React.createElement(Suspense, { fallback: null },
                    React.createElement(TopMobileDrawer, { toggleDrawer: () => {
                            navigation.toggleSection(SECTION.settings);
                            navigation.toggleSection(SECTION.mobileDrawer, false);
                        }, toggleNewMessageModal: () => {
                            navigation.toggleNewMessageModal();
                            navigation.toggleSection(SECTION.mobileDrawer, false);
                        } }))) : null,
                React.createElement(MountOnOpen, { open: navigation.isTemplateMessagesDrawerOpen },
                    React.createElement(TemplateMessagesDrawer, { isOpen: navigation.isTemplateMessagesDrawerOpen, toggleTemplateMessagesDrawer: () => navigation.toggleTemplateMessagesDrawer(), toggleBulkMessagesWizard: () => {
                            wizardStore.setWizardIsOpen();
                            navigation.toggleTemplateMessagesDrawer();
                        }, toggleNewTemplateDrawer: () => {
                            templateMessagesStore.toggleNewTemplateDrawer();
                            navigation.toggleTemplateMessagesDrawer();
                        }, toggleBulkReportsDrawer: () => {
                            navigation.toggleBulkReportsDrawer();
                            navigation.toggleTemplateMessagesDrawer();
                        } })),
                React.createElement(MountOnOpen, { open: navigation.isNewMessageModalOpen },
                    React.createElement(NewMessageModal, { toggleNewMessageModal: () => navigation.toggleNewMessageModal(), isOpen: navigation.isNewMessageModalOpen, setChat: this.createChat })),
                React.createElement(MountOnOpen, { open: navigation.isBulkReportsDrawerOpen },
                    React.createElement(BulkReportsDrawer, { toggleBulkReportsDrawer: () => navigation.toggleBulkReportsDrawer(), toggleBulkMessagesWizard: () => wizardStore.setWizardIsOpen(), isOpen: navigation.isBulkReportsDrawerOpen })),
                React.createElement(MountOnOpen, { open: navigation.isEditProfileDrawerOpen },
                    React.createElement(EditProfileDrawer, { toggleEditProfileDrawer: () => navigation.toggleEditProfileDrawer(), isOpen: navigation.isEditProfileDrawerOpen })),
                React.createElement(MountOnOpen, { open: !!sectionOpen.notificationExample },
                    React.createElement(NotificationSubscribeExample, { isOpen: !!sectionOpen.notificationExample, setNotificationExampleOpen: () => navigation.toggleSection(SECTION.notificationExample) })),
                React.createElement(MountOnOpen, { open: this.props.location.pathname.startsWith('/manage/chats/') },
                    React.createElement(ChatSettingsRoute, null)),
                React.createElement("div", { className: "workspace-page" },
                    showMobilePageHeader && React.createElement(MobileTopBar, { backToInbox: true }),
                    React.createElement("div", { className: "workspace-content" },
                        React.createElement(Suspense, { fallback: React.createElement(LoadingScreen, null) },
                            React.createElement(Switch, null,
                                React.createElement(Route, { path: "/bot-admin", component: BotAdmin }),
                                React.createElement(ProtectedRoute, { exact: true, path: "/dashboard", requiredPermissions: [Permissions.Permission.stats_read], component: DashboardPage }),
                                React.createElement(Route, { path: "/manage/scenarios/:scenarioId?" },
                                    React.createElement(ErrorBoundary, null,
                                        React.createElement(ScenariosManager, null))),
                                React.createElement(Route, null,
                                    React.createElement("div", { className: "content-container" },
                                        React.createElement(TopContent, null),
                                        React.createElement("div", { className: "content-container-inner" },
                                            React.createElement(CenterContent, null)),
                                        React.createElement(RightBar, null)))))))),
            appState === APP_STATE.ERROR ? React.createElement(IsReadyErrorMessage, null) : null,
            React.createElement(ShowOffline, null),
            !!logoutCountdownLength && !!logoutCountdownStart && logoutCountdown && (
            // Don't use "visible" property to hide modal and unmount always,
            // because otherwise it continues to count down in the background and
            // eventually logs out the user even if the modal is not visible
            React.createElement(EmptyModal, { visible: true },
                React.createElement(AutoDisconnectModalContent, { countdownLength: logoutCountdownLength, inactivityPeriod: logoutCountdownStart, onLogout: loginStore.logOut })))));
    }
}
const ShowOffline = observer(function () {
    if (onlineStatus.online) {
        return null;
    }
    else {
        return React.createElement(NoInternetMessage, null);
    }
});
export default withTranslation()(withStore(observer(MainPageLayout)));
